import { isBotUserAgent } from '~/utils/isBotUserAgent';

/**
 * Mixpanel Plugin - Initializes Mixpanel analytics on client-side
 *
 * Uses requestIdleCallback to defer Mixpanel loading until browser is idle.
 * This ensures zero impact on LCP, FCP, and TBT.
 */
export default defineNuxtPlugin({
	name: 'mixpanel',
	setup() {
		const runtimeConfig = useRuntimeConfig();
		const mixpanelEnv = runtimeConfig.public.mixpanel;
		const token = mixpanelEnv.token as string;

		// Skip if no token is configured
		if (!token || token.trim() === '') {
			return;
		}

		// Skip for known crawlers. Mixpanel's built-in blocklist
		// (isBlockedUA) misses HeadlessChrome, semrush, AI crawlers and
		// generic tools — this closes that gap before the SDK even loads.
		if (isBotUserAgent()) {
			return;
		}

		// Defer Mixpanel initialization to idle time
		const initializeMixpanel = async () => {
			try {
				// Dynamic import to avoid bundling in main chunk
				const [{ mixpanelService }, { version }] = await Promise.all([
					import('~/core/service/mixpanel/mixpanel'),
					import('~~/package.json')
				]);

				const MIXPANEL_INSTANCE_KEY = 'mixpanel-instance';
				const debug = mixpanelEnv.debug as boolean;
				const proxyUrl = (mixpanelEnv.proxy_url as string) || undefined;
				const autoPageviewBlockedUrls = [
					/\/imovel\/[^/?#]+(?:\/[^/?#]+)?(?:[?#]|$)/,
					/\/shorts\/[^/?#]+(?:[?#]|$)/,
					/:\/\/[^/]+\/(?:[?#]|$)/, // Home
					/\/(venda|aluguel)\/imoveis/ // SRP
				];

				const { user } = useUserSession();

				const mixpanelConfig = {
					track_pageview: false,
					autocapture: {
						pageview: true,
						click: false,
						dead_click: false,
						input: false,
						scroll: false,
						submit: false,
						rage_click: false,
						block_url_regexes: autoPageviewBlockedUrls
					},
					debug,
					api_host: proxyUrl
				};

				const mixpanel = mixpanelService(
					token,
					mixpanelConfig,
					MIXPANEL_INSTANCE_KEY,
					version
				);

				await mixpanel.init();
				mixpanel.config(user.value);

				// Store the instance for later access
				const nuxtApp = useNuxtApp();
				nuxtApp.provide('mixpanel', mixpanel);
			} catch (error) {
				console.error('Failed to initialize Mixpanel:', error);
			}
		};

		// Wait for browser idle time before loading Mixpanel
		if (typeof window !== 'undefined') {
			if ('requestIdleCallback' in window) {
				requestIdleCallback(() => initializeMixpanel(), { timeout: 5000 });
			} else {
				// Fallback for browsers without requestIdleCallback
				setTimeout(initializeMixpanel, 2500);
			}
		}
	}
});
